import { useId } from "react";

import { useDialog } from "../interaction";
import {
  boardChrome,
  ClipboardClip,
  ClipboardCloseButton,
  sheetSurface,
  type ClipScale,
} from "./clipboardChrome";
import { cn } from "../lib/cn";

export type ClipboardSize = "sm" | "md" | "lg" | "takeover";

/* The takeover keeps an equal desk margin all round, capped at 64rem wide. */
const dialogSize: Record<ClipboardSize, string> = {
  sm: "max-h-[85vh] w-[calc(100vw-2rem)] max-w-sm",
  md: "max-h-[85vh] w-[calc(100vw-2rem)] max-w-xl",
  lg: "max-h-[85vh] w-[calc(100vw-2rem)] max-w-3xl",
  takeover:
    "h-[calc(100vh-clamp(2rem,8vw,6rem))] w-[calc(100vw-clamp(2rem,8vw,6rem))] max-w-5xl",
};

/* The physical object shrinks with the dialog: clip jaw, hinge hump and board
   margins all step down together (clipboardChrome) so a small clipboard keeps
   its proportions instead of wearing a takeover's clip. */
const chromeScale: Record<ClipboardSize, ClipScale> = {
  sm: "sm",
  md: "md",
  lg: "lg",
  takeover: "xl",
};

/*
 * Handed to you: the clipboard rises from below and settles as it lands. Same
 * @starting-style + allow-discrete scheme as Sheet, animating `translate` as
 * its own property rather than through `transform`. The travel scales with the
 * object: a small clipboard is handed over a shorter distance, faster (200ms at
 * sm), so the settle reads at every size without the small ones feeling thrown.
 */
const motionBase = cn(
  "transition-[opacity,translate,overlay,display] transition-discrete ease-paper",
  "opacity-0 open:opacity-100 starting:open:opacity-0",
  "open:translate-y-0",
  "motion-reduce:transition-none",
);

const motionBySize: Record<ClipboardSize, string> = {
  sm: cn("duration-200 translate-y-4", "starting:open:translate-y-4"),
  md: cn("duration-250 translate-y-6", "starting:open:translate-y-6"),
  lg: cn("duration-300 translate-y-8", "starting:open:translate-y-8"),
  takeover: cn("duration-300 translate-y-10", "starting:open:translate-y-10"),
};

const backdropBase = cn(
  "backdrop:bg-desk/60 backdrop:backdrop-blur-[2px]",
  "backdrop:transition-[opacity,display] backdrop:transition-discrete backdrop:ease-paper",
  "backdrop:opacity-0 open:backdrop:opacity-100 starting:open:backdrop:opacity-0",
  "motion-reduce:backdrop:transition-none",
);

const backdropDuration: Record<ClipboardSize, string> = {
  sm: "backdrop:duration-200",
  md: "backdrop:duration-250",
  lg: "backdrop:duration-300",
  takeover: "backdrop:duration-300",
};

/* Paper padding follows the object: a small sheet is not set with a
   takeover's margins. */
const headerPad: Record<ClipboardSize, string> = {
  sm: "px-5 pt-5 pb-3",
  md: "px-6 pt-6 pb-3",
  lg: "px-6 pt-7 pb-4 sm:px-8",
  takeover: "px-6 pt-7 pb-4 sm:px-8",
};

const titleType: Record<ClipboardSize, string> = {
  sm: "text-lg",
  md: "text-lg",
  lg: "text-xl",
  takeover: "text-xl",
};

const bodyPad: Record<ClipboardSize, string> = {
  sm: "px-5 py-4",
  md: "px-6 py-4",
  lg: "px-6 py-5 sm:px-8",
  takeover: "px-6 py-5 sm:px-8",
};

const footerPad: Record<ClipboardSize, string> = {
  sm: "px-5 py-3",
  md: "px-6 py-4",
  lg: "px-6 py-4 sm:px-8",
  takeover: "px-6 py-4 sm:px-8",
};

export interface ClipboardProps extends Omit<
  React.DialogHTMLAttributes<HTMLDialogElement>,
  "open" | "title" | "children" | "onClose" | "onCancel"
> {
  open: boolean;
  /** Called for every user-driven close: Escape, backdrop, the close button. */
  onClose: () => void;
  /** Rendered on the sheet under the clip and wired up as the accessible name. */
  title: React.ReactNode;
  children: React.ReactNode;
  /** Actions row pinned to the bottom of the sheet. Omit for a bare form. */
  footer?: React.ReactNode;
  /**
   * How much desk the clipboard takes. `takeover` (the default) is the
   * near-full-viewport board; `sm`/`md`/`lg` are centered dialogs sized to
   * their content, still the same physical object.
   */
  size?: ClipboardSize;
  /** When false, Escape and backdrop clicks are ignored and the close button is hidden. */
  dismissable?: boolean;
  className?: string;
  ref?: React.Ref<HTMLDialogElement>;
}

/**
 * The modal object: a clipboard handed to you to deal with before you
 * continue. Every piece of paperwork rides it - confirms, forms, cheat
 * sheets, full takeovers - sized to its content via `size`. The one modal
 * that is not paperwork (the PimsAudit screenshot lightbox, a photo held up)
 * stays on the plain Sheet.
 *
 * Native props land on the `<dialog>` itself. `open` is not one of them: the
 * element's open state is driven imperatively by `useDialog` (showModal, not
 * the attribute), so the prop of that name is the React-side request.
 */
export function Clipboard({
  open,
  onClose,
  title,
  children,
  footer,
  size = "takeover",
  dismissable = true,
  className,
  ref,
  ...rest
}: ClipboardProps) {
  const titleId = useId();
  const { dialogRef, handleCancel, handleClose } = useDialog({
    open,
    onClose,
    dismissable,
  });

  const scale = chromeScale[size];

  return (
    <dialog
      {...rest}
      /* useDialog owns the element, so a caller's ref rides alongside it. */
      ref={(node) => {
        dialogRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      aria-labelledby={titleId}
      onCancel={handleCancel}
      onClose={handleClose}
      className={cn(
        /* Preflight zeroes the UA's `margin: auto`, so centering is ours to
           restore. Display is `open:flex`, never a base `flex` - an
           unconditional display would override the UA's
           `dialog:not([open]) { display: none }` and leave a closed clipboard
           invisibly in flow (see Sheet). The dialog itself is transparent:
           the board child is the visible object, which also makes useDialog's
           backdrop hit-test (against firstElementChild) treat clicks on the
           board as content, not as a dismiss.

           whitespace-normal and text-left: the dialog element lives wherever
           its trigger is mounted, and inherited text properties follow it into
           the top layer. A trigger inside a nowrap, right-aligned host (a
           ledger's action cell) would otherwise hand the whole modal
           `white-space: nowrap` - under which the sheet's break-words has no
           wrap opportunities to work with and every paragraph runs off the
           paper sideways - and `text-align: right`.

           outline-none: useDialog parks the initial focus on the dialog
           element itself (so no control paints a focus ring the moment the
           paperwork lands); the dialog is not a Tab stop, so it never needs a
           ring of its own. */
        "m-auto flex-col whitespace-normal text-left bg-transparent p-0 font-body text-ink outline-none open:flex",
        /* The UA gives every dialog `overflow: auto`, so anything the sheet
           cannot fit turns the whole board into a sideways scroller. The paper
           wraps its own content (below), and this pins the inline axis so no
           residual pixel can drag the clipboard off-centre; vertical scrolling
           stays where it belongs, on the body. */
        "overflow-x-hidden",
        dialogSize[size],
        motionBase,
        motionBySize[size],
        backdropBase,
        backdropDuration[size],
        className,
      )}
    >
      {/* The board. The top padding (boardChrome) leaves a pressboard strip
          for the clip and the close button; the sheet hangs from under the
          clip. The takeover fills its fixed-height dialog (flex-1); the
          dialog sizes shrink-to-fit their content under the max-height. */}
      <div
        className={cn(
          "flex min-h-0 flex-col",
          size === "takeover" && "flex-1",
          boardChrome(scale),
        )}
      >
        {/* The sheet: the paper everything lives on. The sheet is a fixed width
            for its size, so nothing written on it may push it wider: titles are
            unbounded free text (a display name, a patient name) and a single
            unbreakable run - an email, a URL - would otherwise spill off the
            paper. `overflow-wrap` inherits, so one declaration here covers the
            header, the body and the footer. */}
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col break-words",
            sheetSurface,
          )}
        >
          <header className={cn("border-b border-rule", headerPad[size])}>
            <h2
              id={titleId}
              className={cn(
                "text-center font-display font-medium tracking-tight text-ink",
                titleType[size],
              )}
            >
              {title}
            </h2>
          </header>

          {/* min-h-0 is what lets the body shrink inside the flex column and scroll. */}
          <div
            className={cn(
              "min-h-0 flex-1 overflow-y-auto text-ink-body",
              bodyPad[size],
            )}
          >
            {children}
          </div>

          {footer && (
            <div
              className={cn(
                /* The actions row is the one inline-axis layout on the sheet, so
                   it is also the one place a flex item's automatic minimum size
                   applies: two long labels cannot shrink below their text and
                   would push the paper wider. Wrapping is the graceful out. */
                "flex flex-wrap justify-end gap-3 rounded-b-paper border-t border-rule bg-recess",
                footerPad[size],
              )}
            >
              {footer}
            </div>
          )}
        </div>

        <ClipboardClip scale={scale} />

        {/* Close lives on the board, not the paper: the shared bare X
            (clipboardChrome), which the search palette rides too. */}
        {dismissable && (
          <ClipboardCloseButton scale={scale} onClose={onClose} />
        )}
      </div>
    </dialog>
  );
}
